import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, FileText, Search } from 'lucide-react';
import { getMyPaymentHistory, getMyUplinePaymentHistory, type Dict } from '../../../api/management';

const FILTERS = [
  { value: 'all', label: 'All', activeClass: 'crystal-sapphire' },
  { value: 'credit', label: 'Credit', activeClass: 'crystal-violet' },
  { value: 'cash', label: 'Cash', activeClass: 'crystal-emerald' },
  { value: 'account', label: 'Account', activeClass: 'crystal-ruby' },
  { value: 'manual_account', label: 'Manual Account', activeClass: 'crystal-smoky' },
] as const;

type FilterValue = (typeof FILTERS)[number]['value'];

const PAGE_SIZE = 50;

const rowsFrom = (value: Dict | null): Dict[] => {
  if (!value) return [];
  for (const key of ['rows', 'transactions', 'items', 'results']) {
    const rows = value[key];
    if (Array.isArray(rows)) return rows as Dict[];
  }
  return [];
};

const money = (value: unknown) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? Math.trunc(number).toLocaleString('en-US') : '0';
};

const formatDateTime = (value: unknown) => {
  const raw = String(value ?? '');
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw || '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const uplineTone = (value: unknown) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number) || number === 0) return 'admin-upline-neutral';
  return number < 0 ? 'admin-upline-negative' : 'admin-upline-positive';
};

const settlementFrom = (row: Dict): Dict | null => (
  row.settlementDetail && typeof row.settlementDetail === 'object'
    ? row.settlementDetail as Dict
    : null
);

const detailText = (row: Dict) => {
  const settlement = settlementFrom(row);
  if (settlement) {
    const title = String(settlement.drawTitle ?? 'Draw Settlement');
    const date = String(settlement.date ?? '');
    const time = String(settlement.drawTime ?? '');
    return [title, date, time].filter(Boolean).join(' · ');
  }
  return String(row.description ?? row.detail ?? '—');
};

const netBillTone = (value: unknown) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number) || number === 0) return 'admin-upline-neutral';
  return number > 0 ? 'admin-upline-negative' : 'admin-upline-positive';
};

export default function BookDetailsPage() {
  const initial = useMemo(() => new URLSearchParams(window.location.search), []);
  const source = initial.get('source') === 'upline' ? 'upline' : 'all';
  const [type, setType] = useState<FilterValue>(() => {
    const value = (initial.get('type') ?? 'all').toLowerCase();
    return FILTERS.some((item) => item.value === value) ? value as FilterValue : 'all';
  });
  const [start, setStart] = useState(initial.get('start') ?? '');
  const [end, setEnd] = useState(initial.get('end') ?? '');
  const [search, setSearch] = useState(initial.get('search') ?? '');
  const [data, setData] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const query = useCallback(() => {
    const params = new URLSearchParams();
    if (type !== 'all') params.set('type', type.toUpperCase());
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    if (search.trim()) params.set('search', search.trim());
    return params.toString();
  }, [end, search, start, type]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const suffix = query();
      const result = source === 'upline'
        ? await getMyUplinePaymentHistory(suffix)
        : await getMyPaymentHistory(suffix);
      setData(result);
      setPage(1);

      const url = new URL(window.location.href);
      ['type', 'start', 'end', 'search'].forEach((key) => url.searchParams.delete(key));
      if (type !== 'all') url.searchParams.set('type', type);
      if (start) url.searchParams.set('start', start);
      if (end) url.searchParams.set('end', end);
      if (search.trim()) url.searchParams.set('search', search.trim());
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to load transaction history.');
    } finally {
      setLoading(false);
    }
  }, [end, query, search, source, start, type]);

  useEffect(() => {
    const timer = window.setTimeout(() => { void load(); }, search.trim() ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  const rows = rowsFrom(data);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pdfBase = source === 'upline' ? '/api/my/upline-payment-history/pdf' : '/api/my/payment-history/pdf';
  const pdfHref = `${pdfBase}${query() ? `?${query()}` : ''}`;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 p-3 sm:p-5">
      <section className="admin-border rounded-2xl border bg-[rgba(15,23,42,.18)] p-4 shadow-[0_10px_30px_rgba(0,0,0,.12)] sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="admin-text-muted text-[10px] font-black uppercase tracking-[0.16em]">Book Details</p>
            <h1 className="admin-text-strong mt-1 text-xl font-black sm:text-2xl">Transaction History</h1>
            <p className="admin-text-muted mt-1 text-xs font-semibold">Filter and review account activity</p>
          </div>
          {loading ? <span className="admin-text-muted text-xs font-bold">Loading…</span> : <span className="admin-text-muted text-xs font-bold">{rows.length} records</span>}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setType(item.value)}
              className={`crystal-btn min-h-9 rounded-xl px-3 py-2 text-xs font-black ${type === item.value ? item.activeClass : 'crystal-action-smoky'}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          <label className="block">
            <span className="admin-text-muted mb-1.5 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide"><CalendarDays size={13} /> Start Date</span>
            <input type="date" value={start} onChange={(event) => setStart(event.target.value)} className="w-full rounded-xl border px-2.5 py-2 text-xs font-bold sm:px-3 sm:text-sm" />
          </label>
          <label className="block">
            <span className="admin-text-muted mb-1.5 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide"><CalendarDays size={13} /> End Date</span>
            <input type="date" value={end} onChange={(event) => setEnd(event.target.value)} className="w-full rounded-xl border px-2.5 py-2 text-xs font-bold sm:px-3 sm:text-sm" />
          </label>
          <label className="col-span-2 block lg:col-span-2">
            <span className="admin-text-muted mb-1.5 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide"><Search size={13} /> Search</span>
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search transactions" className="w-full rounded-xl border px-3 py-2.5 text-sm font-bold" />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <a href={pdfHref} target="_blank" rel="noreferrer" className="crystal-btn crystal-smoky inline-flex min-h-9 items-center gap-2 rounded-xl px-4 py-2 text-xs font-black">
            <FileText size={14} /> Export PDF
          </a>
        </div>

        {error ? <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-sm font-bold text-rose-300">{error}</div> : null}
      </section>

      <section className="admin-border overflow-hidden rounded-2xl border bg-[rgba(15,23,42,.18)] shadow-[0_10px_30px_rgba(0,0,0,.12)]">
        <div className="hidden">
          {loading && !rows.length ? <p className="admin-text-muted p-6 text-center text-sm font-bold">Loading transaction history…</p> : null}
          {!loading && !rows.length ? <p className="admin-text-muted p-6 text-center text-sm font-bold">No transactions match the selected filters.</p> : null}
          {rows.length ? (
            <div className="divide-y divide-white/[0.08]">
              {pagedRows.map((row, index) => (
                <article key={String(row.id ?? index)} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="admin-text-muted text-[10px] font-black uppercase tracking-wide">{formatDateTime(row.createdAt)}</p>
                      <p className="admin-text-strong mt-1 text-sm font-black">{String(row.paymentType ?? '—')}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wide ${String(row.operation ?? '').toUpperCase() === 'WITHDRAW' ? 'bg-rose-500/10 text-rose-300 ring-1 ring-rose-400/25' : 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/25'}`}>{String(row.operation ?? '—')}</span>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="admin-subpanel rounded-xl p-2.5"><p className="admin-text-muted text-[8px] font-black uppercase">Amount</p><p className="admin-text-strong mt-1 text-sm font-black tabular-nums">{money(row.amount)}</p></div>
                    <div className="admin-subpanel rounded-xl p-2.5"><p className="admin-text-muted text-[8px] font-black uppercase">Upline</p><p className={`mt-1 text-sm font-black tabular-nums ${uplineTone(row.upline)}`}>{money(row.upline)}</p></div>
                    <div className="admin-subpanel rounded-xl p-2.5"><p className="admin-text-muted text-[8px] font-black uppercase">Balance</p><p className="admin-balance-text mt-1 text-sm font-black tabular-nums">{money(row.balance)}</p></div>
                  </div>

                  <div className="admin-border mt-3 rounded-xl border px-3 py-2">
                    <p className="admin-text-muted text-[8px] font-black uppercase tracking-wide">Detail</p>
                    <p className="admin-text mt-1 break-words text-xs font-semibold">{detailText(row)}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </div>

        <div className="max-h-[65vh] overflow-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[860px] border-collapse text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="admin-border border-b">
                <th className="px-4 py-3 text-left font-black uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-right font-black uppercase tracking-wide">Upline</th>
                <th className="px-4 py-3 text-left font-black uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left font-black uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-right font-black uppercase tracking-wide">Amount</th>
                <th className="px-4 py-3 text-right font-black uppercase tracking-wide">Balance</th>
                <th className="px-4 py-3 text-left font-black uppercase tracking-wide">Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length ? <tr><td colSpan={7} className="admin-text-muted px-4 py-8 text-center font-bold">Loading transaction history…</td></tr> : null}
              {!loading && !rows.length ? <tr><td colSpan={7} className="admin-text-muted px-4 py-8 text-center font-bold">No transactions match the selected filters.</td></tr> : null}
              {pagedRows.map((row, index) => (
                <tr key={String(row.id ?? index)} className="admin-border border-b last:border-b-0">
                  <td className="admin-text whitespace-nowrap px-4 py-3 font-semibold">{formatDateTime(row.createdAt)}</td>
                  <td className={`whitespace-nowrap px-4 py-3 text-right font-black tabular-nums ${uplineTone(row.upline)}`}>{money(row.upline)}</td>
                  <td className="admin-text-strong whitespace-nowrap px-4 py-3 font-bold">{String(row.paymentType ?? '—')}</td>
                  <td className="admin-text whitespace-nowrap px-4 py-3 font-bold">{String(row.operation ?? '—')}</td>
                  <td className="admin-text-strong whitespace-nowrap px-4 py-3 text-right font-black tabular-nums">{money(row.amount)}</td>
                  <td className="admin-balance-text whitespace-nowrap px-4 py-3 text-right font-black tabular-nums">{money(row.balance)}</td>
                  <td className="admin-text px-4 py-3 font-semibold">
                    {settlementFrom(row) ? (
                      <div className="min-w-[440px]">
                        <div className="flex items-center justify-between gap-3">
                          <p className="admin-text-strong text-xs font-black">{String(settlementFrom(row)?.drawTitle ?? 'Draw Settlement')}</p>
                          <p className="admin-text-muted whitespace-nowrap text-[10px] font-bold">{[String(settlementFrom(row)?.date ?? ''), String(settlementFrom(row)?.drawTime ?? '')].filter(Boolean).join(' · ')}</p>
                        </div>
                        <div className="mt-1 grid grid-cols-5 gap-1">
                          <div className="admin-subpanel rounded-md px-1.5 py-1 text-center">
                            <p className="admin-text-muted text-[7px] font-black uppercase tracking-wide">Total Sale</p>
                            <p className="admin-text-strong mt-0.5 text-[10px] font-black tabular-nums">{money(settlementFrom(row)?.totalSale)}</p>
                          </div>
                          <div className="admin-subpanel rounded-md px-1.5 py-1 text-center">
                            <p className="admin-text-muted text-[7px] font-black uppercase tracking-wide">Commission</p>
                            <p className="admin-text-strong mt-0.5 text-[10px] font-black tabular-nums">{money(settlementFrom(row)?.commission)}</p>
                          </div>
                          <div className="admin-subpanel rounded-md px-1.5 py-1 text-center">
                            <p className="admin-text-muted text-[7px] font-black uppercase tracking-wide">Prize</p>
                            <p className="admin-text-strong mt-0.5 text-[10px] font-black tabular-nums">{money(settlementFrom(row)?.prize)}</p>
                          </div>
                          <div className="admin-subpanel rounded-md px-1.5 py-1 text-center">
                            <p className="admin-text-muted text-[7px] font-black uppercase tracking-wide">Share</p>
                            <p className="admin-text-strong mt-0.5 text-[10px] font-black tabular-nums">{money(settlementFrom(row)?.share)}</p>
                          </div>
                          <div className="admin-subpanel rounded-md px-1.5 py-1 text-center">
                            <p className="admin-text-muted text-[7px] font-black uppercase tracking-wide">Net Bill</p>
                            <p className={`mt-0.5 text-[10px] font-black tabular-nums ${netBillTone(settlementFrom(row)?.netBill)}`}>{money(settlementFrom(row)?.netBill)}</p>
                          </div>
                        </div>
                      </div>
                    ) : <span className="block max-w-[320px]">{detailText(row)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > PAGE_SIZE ? (
          <div className="admin-border flex flex-wrap items-center justify-between gap-2 border-t px-3 py-3 sm:px-4">
            <p className="admin-text-muted text-[10px] font-bold">
              Page {safePage} of {totalPages} · {rows.length} records · 50 per page
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                className="crystal-btn crystal-action-smoky min-h-8 rounded-lg px-3 py-1.5 text-[10px] font-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                className="crystal-btn crystal-sapphire min-h-8 rounded-lg px-3 py-1.5 text-[10px] font-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
